import React, { Fragment, useMemo } from 'react'
import { useGlobalFilter, useGroupBy, useRowSelect, useSortBy, useTable } from 'react-table'
import { useSticky } from 'react-table-sticky'
import { GlobalFilter, FormGroup } from '../../../../../components'
import { useLazyEffect } from '../../../../../utils/hooks'

const TableView = (props) => {
  const {
    columns,
    data,
    getHeaderProps,
    getColumnProps,
    getRowProps,
    getCellProps,
    searchKeyword,
    setSearchKeyword,
    optimized,
    setOptimized,
    getTableData,
    selectedRowIds,
    getRowId,
    hiddenColumns,
  } = props

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = useTable(
    {
      columns,
      data,
      getRowId,
      initialState: {
        sortBy: [{ id: 'ici', desc: false }],
        selectedRowIds,
        hiddenColumns,
      },
      useControlledState: (state) =>
        useMemo(() => ({ ...state, globalFilter: searchKeyword }), [state, searchKeyword]),
    },
    useGlobalFilter,
    useGroupBy,
    useSortBy,
    useRowSelect,
    useSticky,
  )

  const renderedRows = useMemo(
    () => (optimized && rows.length > 10 ? rows.slice(0, 10) : rows),
    [rows, optimized],
  )

  useLazyEffect(() => {
    getTableData(renderedRows.map((item) => item.original) ?? [])
  }, [optimized, state.globalFilter, state.sortBy])

  return (
    <Fragment>
      <div className="tableInfoBar">
        <GlobalFilter setGlobalFilter={setSearchKeyword} globalFilter={state.globalFilter} />
        <div className="tableResults">
          <FormGroup className="tableToggleSwitch">
            <span>Show Optimized Models</span>
            <input
              type="checkbox"
              className="toggleSwitchInput"
              name="optimized"
              checked={optimized}
              onChange={setOptimized}
              disabled={data.length <= 10}
            />
          </FormGroup>
        </div>
        <div className="tableResults">
          {`Showing ${renderedRows.length} entr${renderedRows.length > 1 ? 'ies' : 'y'}`}
        </div>
      </div>
      <div className="tableInfoBar printView">
        <span>{searchKeyword && `Search: ${searchKeyword}`}</span>
        <span>Show Optimized Models: {optimized ? 'Yes' : 'No'}</span>
      </div>
      <div className="nonScrollable">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...restHeaderProps } = headerGroup.getHeaderGroupProps()

              return (
                <tr key={key} {...restHeaderProps}>
                  {headerGroup.headers.map((column) => {
                    const { key, ...restColumnProps } = column.getHeaderProps([
                      {
                        className: column.className,
                        style: column.style,
                      },
                      getColumnProps(column),
                      getHeaderProps(column),
                      column.getSortByToggleProps(),
                    ])
                    return (
                      <th
                        key={key}
                        {...restColumnProps}
                        className={`th ${
                          column.isSorted
                            ? column.isSortedDesc
                              ? '-sortDescending'
                              : '-sortAccending'
                            : ''
                        }`}
                      >
                        <div className="tableLabel">
                          {column.render('Header')}
                          <div className="sortIcons">
                            <img src="assets/images/rws/caret-up.svg" className="sortIcons__up" />
                            <img
                              src="assets/images/rws/caret-down.svg"
                              className="sortIcons__down"
                            />
                          </div>
                        </div>
                      </th>
                    )
                  })}
                </tr>
              )
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {renderedRows.map((row, i) => {
              prepareRow(row)
              return (
                <tr key={i} {...row.getRowProps(getRowProps(row))}>
                  {row.cells.map((cell) => {
                    const { key, ...restCellProps } = cell.getCellProps([
                      {
                        className: cell.column.className,
                        style: cell.column.style,
                      },
                      getColumnProps(cell.column),
                      getCellProps(cell),
                    ])
                    return (
                      <td key={key} {...restCellProps}>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>

        {searchKeyword && rows.length === 0 && (
          <div className="notice -warning -noResults">
            <span className="material-icons">info</span>
            <div className="notice__text">
              <div className="notice__header">No Matching Records Found</div>
              <div className="notice__body">Try editing or deleting your search criteria.</div>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  )
}

TableView.defaultProps = {
  sortBy: [],
  getHeaderProps: () => ({}),
  getColumnProps: () => ({}),
  getRowProps: () => ({}),
  getCellProps: () => ({}),
}

export default TableView
